import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, getRouteApi, useBlocker, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { annotationQuery } from '@/api/annotations'
import { assetQuery } from '@/api/assets'
import { API_BASE } from '@/api/client'
import { getJob, type DeepAnnotateResult } from '@/api/jobs'
import { getProposal } from '@/api/proposals'
import { stageDetailsQuery } from '@/api/stages'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'
import { AnnotatorLayout } from '@/features/annotator/AnnotatorLayout'
import { SimpleFixMode } from '@/features/annotator/SimpleFixMode'
import { SpriteEditor } from '@/features/sprites/SpriteEditor'
import { describeSaveError, ensureSaved, useLiveLint } from '@/features/annotator/useAutosave'
import { isStageBody, useEditorStore } from '@/stores/editorStore'

/**
 * `/annotate/$kind/$uid?tool=&sel=&zoom=&x=&y=&proposal=&mode=simple|expert&return=`
 *
 * Asset uids contain `/` (`char:people/royalty/king/king_asian`), so the
 * route is registered as `/annotate/$kind/$` — a splat — and `useAnnotateParams()`
 * hands the rest of the path back as `uid`, verbatim. Nothing is
 * percent-encoded on the way in or out (§5 preamble).
 *
 * This file is the **dispatcher**: it loads the document into `editorStore`,
 * applies the deep-link search params, and then hands over to one of two
 * stage shells — `SimpleFixMode` or `AnnotatorLayout`. Character documents
 * open the spritesheet landmark editor; objects continue to open in Puppets.
 */
export const ANNOTATION_KINDS = ['stage', 'character', 'object'] as const
export type AnnotationKind = (typeof ANNOTATION_KINDS)[number]

export const EDITOR_TOOLS = ['select', 'polygon', 'band', 'rect', 'horizon', 'anchor', 'portal', 'path', 'layer', 'magnifier'] as const
export type EditorTool = (typeof EDITOR_TOOLS)[number]

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const annotateSearchSchema = z.object({
  tool: lenient(z.enum(EDITOR_TOOLS)),
  /** Feature id to pre-select (`r_ground`). */
  sel: lenient(z.string()),
  zoom: lenient(z.coerce.number().min(0.1).max(16)),
  /** Percent coordinates to centre on (0–100). */
  x: lenient(z.coerce.number().min(0).max(100)),
  y: lenient(z.coerce.number().min(0).max(100)),
  proposal: lenient(z.coerce.number().int().positive()),
  mode: lenient(z.enum(['simple', 'expert'])),
  /**
   * Where the return bar goes back to: a story (`/stories/<uid>`) or the background's own page
   * (`/backgrounds/<uid>`). Only same-origin paths are honoured.
   */
  return: lenient(z.string().regex(/^\/(?!\/)/)),
  /** Which scene of that story to re-validate and re-bake on the way back. */
  scene: lenient(z.string()),
})

export type AnnotateSearch = z.infer<typeof annotateSearchSchema>

export function validateAnnotateSearch(input: Record<string, unknown>): AnnotateSearch {
  return annotateSearchSchema.parse(input)
}

export const ANNOTATE_ROUTE_ID = '/annotate/$kind/$' as const
const route = getRouteApi(ANNOTATE_ROUTE_ID)

export interface AnnotateParams {
  kind: AnnotationKind | null
  /** The asset uid, verbatim (`bg:nature/meadow`); empty string if missing. */
  uid: string
}

export function parseAnnotationKind(raw: string | undefined): AnnotationKind | null {
  return (ANNOTATION_KINDS as readonly string[]).includes(raw ?? '') ? (raw as AnnotationKind) : null
}

/** Typed params for the annotator: `kind` validated, `uid` = the splat. */
export function useAnnotateParams(): AnnotateParams {
  const params = route.useParams()
  return { kind: parseAnnotationKind(params.kind), uid: params._splat ?? '' }
}

/** Build the path for a `<Link to>`; the uid goes in verbatim. */
export function annotateHref(kind: AnnotationKind, uid: string): string {
  return `/annotate/${kind}/${uid}`
}

/** `/api/media/{uid}/…` — uids go in verbatim here too. */
export function mediaUrl(uid: string, variant: string): string {
  return `${API_BASE}/api/media/${uid}/${variant}`
}

/* -------------------------------------------------------------------------- */
/* Route component                                                             */
/* -------------------------------------------------------------------------- */

export function AnnotatePage() {
  const { kind, uid } = useAnnotateParams()
  const search = route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const mode = search.mode ?? 'expert'

  const doc = useQuery({ ...annotationQuery(uid), enabled: uid.length > 0 && kind === 'stage' })
  // The picture's own name for the header (a seeded body.title can be a template's: a beach
  // titled "Forest clearing"), and live plates need their duration for the scrubber.
  const asset = useQuery({ ...assetQuery(uid), enabled: uid.length > 0 && kind === 'stage' && doc.isSuccess, retry: false })
  // …and the name the parent gave it in Backgrounds › Details wins over the catalog's.
  const details = useQuery({ ...stageDetailsQuery(uid), enabled: uid.length > 0 && kind === 'stage' && doc.isSuccess, retry: false })

  const load = useEditorStore((s) => s.load)
  const reset = useEditorStore((s) => s.reset)
  const select = useEditorStore((s) => s.select)
  const setTool = useEditorStore((s) => s.setTool)
  const zoomTo = useEditorStore((s) => s.zoomTo)
  const setProposal = useEditorStore((s) => s.setProposal)
  const meta = useEditorStore((s) => s.meta)

  /* Load the document, then apply the deep link ---------------------------- */
  // A save writes its envelope back into this query and the SSE bridge refetches it after a write.
  // Reloading the store on those would clear the selection and the undo history, so: another
  // picture is loaded; a newer revision of THIS picture, while nothing is unsaved here, is taken
  // as one undoable step (history kept); over unsaved edits it is ignored — the next Save answers
  // 409 and the conflict dialog lets the person choose. The deep link applies once per picture.
  const deepLinked = useRef<string | null>(null)
  useEffect(() => {
    const env = doc.data
    if (!env) return
    const st = useEditorStore.getState()
    const same = st.meta?.uid === env.asset.uid
    if (same && (st.meta!.rev >= env.rev || st.dirty)) return
    if (same) useEditorStore.getState().adoptServerVersion(env)
    else load(env)
    if (deepLinked.current === env.asset.uid) return
    deepLinked.current = env.asset.uid
    if (search.tool) setTool(search.tool)
    if (search.sel) select([search.sel])
    if (search.zoom) zoomTo(search.zoom)
  }, [doc.data, load, search.sel, search.tool, search.zoom, select, setTool, zoomTo])

  // Leaving throws the editor's state away. Nothing is sent: unsaved changes are the leave
  // dialog's business (below), and a save only ever happens when the person asks for one.
  useEffect(() => () => reset(), [reset])

  // The lints (red shapes, the Check tab) follow every edit through the dry-run lint endpoint.
  useLiveLint(kind === 'stage')

  /* Leaving with unsaved changes asks first -------------------------------- */
  const blocker = useBlocker({ shouldBlockFn: shouldBlockLeave, enableBeforeUnload: hasUnsavedChanges, withResolver: true })
  const [leaveSaving, setLeaveSaving] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)
  useEffect(() => {
    if (blocker.status !== 'blocked') setLeaveError(null)
  }, [blocker.status])
  const saveAndLeave = async () => {
    setLeaveSaving(true)
    setLeaveError(null)
    const ok = await ensureSaved(qc)
    setLeaveSaving(false)
    if (ok) {
      blocker.proceed?.()
      return
    }
    const err = useEditorStore.getState().saveError
    if (err?.kind === 'conflict') {
      // The conflict dialog (Save controls) takes over: pick a version, then leave.
      blocker.reset?.()
      toast.warning('Someone else saved this picture meanwhile', { description: 'Choose which version to keep, then leave.' })
      return
    }
    setLeaveError(describeSaveError(err) ?? 'The picture could not be saved.')
  }

  /* A `?proposal=` deep link puts the review bar on screen ----------------- */
  useEffect(() => {
    if (!search.proposal) {
      setProposal(null)
      return
    }
    let cancelled = false
    void qc
      .fetchQuery({ queryKey: ['proposals', 'detail', search.proposal], queryFn: ({ signal }) => getProposal(search.proposal!, signal) })
      .then(async (p) => {
        // A link to another picture's suggestions must not open them over this one.
        if (cancelled || p.asset_uid !== uid) return
        setProposal({
          id: p.id,
          assetUid: p.asset_uid,
          body: p.body,
          diff: p.diff ?? { added: [], changed: [], unchanged: [], removed: [], locked_skipped: [] },
          view: 'overlay',
          threshold: 0.75,
          source: p.source,
          model: p.model,
          flags: p.flags,
        })
        // A deep AI run keeps what its checks found on the job, not on the proposal row.
        const jobId = (p as { job_id?: number | null }).job_id
        if (!jobId) return
        const job = await getJob(jobId).catch(() => null)
        const result = job?.result as Partial<DeepAnnotateResult> | undefined
        const current = useEditorStore.getState().proposal
        if (cancelled || !result || current?.id !== p.id) return
        setProposal({
          ...current,
          jobId,
          ...(result.findings ? { findings: result.findings } : {}),
          ...(result.mode ? { mode: result.mode } : {}),
          ...(typeof result.cost_usd === 'number' ? { costUsd: result.cost_usd } : {}),
          ...(result.model ? { model: result.model } : {}),
          ...(result.provider ? { provider: result.provider } : {}),
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [qc, search.proposal, setProposal, uid])

  const goto = (to: string) => void navigate({ to, replace: false })
  const setMode = (next: 'simple' | 'expert') =>
    void navigate({ to: '.', search: (old: AnnotateSearch) => ({ ...old, mode: next }), replace: true })

  if (!kind) {
    return (
      <div className="p-8">
        <h1 className="text-lg font-semibold text-ink">That is not something the studio can annotate</h1>
        <p className="mt-1 text-sm text-ink-2">Expected a stage, a character or an object.</p>
      </div>
    )
  }

  if (kind === 'character') return <div className="mx-auto max-w-[1400px] p-4 md:p-8"><SpriteEditor key={uid} uid={uid} /></div>

  if (kind !== 'stage') {
    return (
      <div className="p-8">
        <h1 className="text-lg font-semibold text-ink">Open this object in Puppets</h1>
        <p className="mt-1 text-sm text-ink-2">
          Open Puppets to make this object move.
        </p>
        <Link to="/puppets" className="mt-3 inline-flex min-h-10 items-center text-sm font-medium text-accent hover:underline">
          Go to Puppets
        </Link>
      </div>
    )
  }

  if (doc.isPending) return <p className="p-8 text-sm text-ink-2">Opening {uid}…</p>
  if (doc.isError) {
    const err = doc.error as { status?: number; message?: string }
    return (
      <div className="p-8">
        <h1 className="text-lg font-semibold text-ink">{err.status === 404 ? 'Nothing to annotate here' : 'Could not open this'}</h1>
        <p className="mt-1 text-sm text-ink-2">
          {err.status === 404 ? 'Sounds have no picture to mark up, so the studio does not keep a document for them.' : (err.message ?? 'Unknown error.')}
        </p>
      </div>
    )
  }

  const plate = mediaUrl(uid, 'preview.webp')
  const isLive = doc.data?.asset.kind === 'live_background'
  const title = details.data?.name || asset.data?.name || (isStageBody(doc.data?.body) ? doc.data.body.title : undefined) || uid

  const leaveDialog = (
    <UnsavedChangesDialog
      open={blocker.status === 'blocked'}
      saving={leaveSaving}
      error={leaveError}
      onSave={() => void saveAndLeave()}
      onLeave={() => blocker.proceed?.()}
      onStay={() => blocker.reset?.()}
    />
  )

  if (mode === 'simple') {
    return (
      <>
        <SimpleFixMode
          plateUrl={isLive ? mediaUrl(uid, 'poster.jpg') : plate}
          video={false}
          returnTo={search.return}
          sceneId={search.scene}
          onNavigate={goto}
          onSwitchToExpert={() => setMode('expert')}
        />
        {leaveDialog}
      </>
    )
  }

  return (
    <>
      <AnnotatorLayout
        title={title}
        plateUrl={isLive ? mediaUrl(uid, 'original') : plate}
        video={isLive}
        videoDuration={asset.data?.duration_s ?? 0}
        returnTo={search.return}
        sceneId={search.scene}
        onNavigate={goto}
        onSwitchToSimple={() => setMode('simple')}
        key={meta?.uid ?? uid}
      />
      {leaveDialog}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Leaving with unsaved changes                                                */
/* -------------------------------------------------------------------------- */

/** Block a move to another page (not a `?mode=` / deep-link change) while something is unsaved. */
function shouldBlockLeave({ current, next }: { current: { pathname: string }; next: { pathname: string } }): boolean {
  return useEditorStore.getState().dirty && current.pathname !== next.pathname
}

/** The browser's own "Leave site?" prompt, only while something is unsaved. */
function hasUnsavedChanges(): boolean {
  return useEditorStore.getState().dirty
}

export interface UnsavedChangesDialogProps {
  open: boolean
  saving: boolean
  error: string | null
  onSave(): void
  onLeave(): void
  onStay(): void
}

/** "You have unsaved changes": Save and leave · Leave without saving · Stay here. */
export function UnsavedChangesDialog({ open, saving, error, onSave, onLeave, onStay }: UnsavedChangesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => (!o && !saving ? onStay() : undefined)}>
      <DialogContent size="sm" hideClose>
        <DialogHeader>
          <DialogTitle>You have unsaved changes</DialogTitle>
          <DialogDescription>Save them before you go? If you leave without saving, your changes to this picture are lost.</DialogDescription>
        </DialogHeader>
        {error ? (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-ink">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" disabled={saving} onClick={onStay}>
            Stay here
          </Button>
          <Button variant="secondary" disabled={saving} onClick={onLeave}>
            Leave without saving
          </Button>
          <Button variant="primary" loading={saving} onClick={onSave} autoFocus>
            Save and leave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
